import { Injectable, InjectionToken, Inject } from '@angular/core';

import { AmplifyService } from 'aws-amplify-angular';
import { User } from './user';
import { Observable, BehaviorSubject } from 'rxjs';
import { AuthState } from './auth-state';
import { LoggerService } from '../logger/logger.service';
import { AuthConfig } from './auth-config';
import { Action, ModalAction } from 'src/app/shared/models/action';

import Auth from '@aws-amplify/auth';
import { AppLoadingService } from 'src/app/shared/services/apploading.service';
import { Router } from '@angular/router';

export const AUTH_CONFIG_TOKEN = new InjectionToken<AuthConfig>('AuthConfig');

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private actualAuthState: AuthState = { state: AuthState.SIGNED_OUT, user: null };
  private userAlreadyCheck: boolean = false;

  private signInAction: SignInAction;
  private signOutAction: SignOutAction;

  private messages = new BehaviorSubject<any>([]);
  messages$ = this.messages.asObservable();


  constructor(
    @Inject(AUTH_CONFIG_TOKEN) config: AuthConfig,
    private amplifyService: AmplifyService,
    private logger: LoggerService,
    private router: Router,
    private als: AppLoadingService) {
    this.amplifyService.auth().configure(config);
    this.initObserver();
    this.initActions();
  }

  private initObserver() {
    this.getAuthStateChange().subscribe(
      awsAuthStatus => {
        this.actualAuthState = awsAuthStatus;
        this.signInAction.active = awsAuthStatus.state !== AuthState.SIGNED_IN;
        this.signOutAction.active = !this.signInAction.active;
      }
    );
  }

  private initActions() {
    this.signInAction = new SignInAction(this);
    this.signOutAction = new SignOutAction(this);
  }

  setMessage(msg: any) {
    this.messages.next(msg?.code ? msg : { code: '', msg: msg });
  }
  getMessage() {
    return this.messages.getValue();
  }

  getAuthStateChange(): Observable<AuthState> {
    return this.amplifyService.authStateChange$;
  }

  changeAuthState(state: string, user: User) {
    //if(this.actualAuthState.user != user || this.actualAuthState.state != state)
    this.amplifyService.setAuthState({ state, user });
  }

  getActualAuthState() {
    return this.actualAuthState;
  }

  getUserdata() {
    if (this.actualAuthState.user == null || !this.actualAuthState.user['signInUserSession'])
      return {};

    let user = this.actualAuthState.user['signInUserSession']['idToken']['payload'];

    if (user?.name)
      user.firstname = user.name.split(' ')[0];

    return user;
  }

  async checkAuthenticatedUser() {
    return await this.getCurrentUser()
      .then(user => {
        if (user) {
          this.actualAuthState.user = user;
        }
        return user;
      })
      .catch((err) => this.cognitoCatch(err));
  }

  isUserAlreadyCheck() {
    return this.userAlreadyCheck;
  }

  async getCurrentUser() {
    let user = null;
    let status = AuthState.SIGNED_OUT;
    try {
      if (Auth !== null) {
        user = await Auth?.currentAuthenticatedUser();
        status = AuthState.SIGNED_IN;
      }
    } catch (err) {
      console.log(err);
    } finally {
      this.userAlreadyCheck = true;
      this.changeAuthState(status, user);
    }
    return user;
  }
  /*
    getCurrentUserSessionGroup(session: CognitoUserSession) {
      const decodePayload = session?.getIdToken()?.decodePayload();
      let groups = '';
      if (decodePayload && decodePayload['cognito:groups']) {
        groups = session.getIdToken().decodePayload()['cognito:groups'];
      }
      return groups;
    }
  */
  getCurrentSession() {
    return Auth.currentSession();
  }
  /*
    getCurrentUserGroup(){
      
      let user = this.getUserdata();
      if(user['cognito:groups'] !== undefined)
        return user['cognito:groups'];
      
      return [];
    }
  */
  async signUp(user: User) {
    await this.als.setLoaded(false, 'auth');
    this.amplifyService.auth()
      .signUp({
        username: user.username,
        password: user.password
      })
      .catch((err) => this.cognitoCatch(err))
      .finally(() => this.als.setLoaded(true, 'auth'));
  }

  async confirmSignUp(user: User): Promise<any> {
    await this.als.setLoaded(false, 'auth');
    return this.amplifyService.auth()
      .confirmSignUp(user.username, user.verificationCode)
      .then(() => {
        this.setMessage({ code: 'needAuthenticate', msg: 'Código confirmado com sucesso, faça seu login' });
        this.changeAuthState(AuthState.SIGN_IN, user);
      })
      .catch((err) => {
        this.cognitoCatch(err)
        throw err;
      })
      .finally(() => this.als.setLoaded(true, 'auth'));
  }

  async resendSignUp(user: User): Promise<string> {
    await this.als.setLoaded(false, 'auth');
    return this.amplifyService.auth()
      .resendSignUp(user.username)
      .then(result => {
        if (result?.CodeDeliveryDetails)
          this.setMessage({ code: 'codesent', msg: 'Código reenviado por ' + result.CodeDeliveryDetails.DeliveryMedium + ' para ' + result.CodeDeliveryDetails.Destination });
      })
      .catch((err) => this.cognitoCatch(err))
      .finally(() => this.als.setLoaded(true, 'auth'));
  }


  /**************** SIGNIN ********************/
  async signIn(user: User): Promise<any> {
    this.setMessage({ code: 'authenticating', msg: 'Aguarde, autenticando seu usuário' });
    await this.als.setLoaded(false, 'auth');
    return this.amplifyService.auth().signIn(user.username, user.password)
      .then(result => {

        this.logger.debug(['signIn', result]);
        let newState = 'signedIn';

        switch (result['challengeName']) {
          case 'SMS_MFA':
          case 'SOFTWARE_TOKEN_MFA': newState = 'confirmSignIn'; break;
          case 'NEW_PASSWORD_REQUIRED': newState = AuthState.REQUIRE_NEW_PASSWORD; break;
          default: this.setMessage({ code: 'authenticated', msg: 'Autenticado, redirecionado...' }); break;
        }
        result.source = 'signIn';
        //result.groups = this.getCurrentUserSessionGroup(result.signInUserSession);

        if (newState != 'signedIn')
          this.changeAuthState(newState, result);
      })
      .catch((err) => {

        if (err.code == 'NotAuthorizedException' && err.message == 'Refresh Token has expired') {
          this.router.navigate(['openModal', 'AuthenticatorComponent']);
          this.cognitoCatch(err);
        } else if (err.code == 'UserNotConfirmedException') {

          this.router.navigate(['openModal', 'AuthenticatorComponent']);
          this.setMessage({ code: 'UserNotConfirmedException', msg: 'Verifique sua caixa de e-mail, enviamos um código para validação' });
          this.changeAuthState(AuthState.CONFIRM_SIGN_UP, user);

        } else if (err.code == 'PasswordResetRequiredException') {

          this.changeAuthState(AuthState.FORGOT_PASSWORD, user);

        } else {

          this.cognitoCatch(err);

        }

        throw (err);
      })
      .finally(() => this.als.setLoaded(true, 'auth'));
  }

  async confirmSignIn(user: User): Promise<any> {
    await this.als.setLoaded(false, 'auth');
    const mfaType = user.challengeParam === 'SOFTWARE_TOKEN_MFA' ? user.challengeParam.requiredAttributes : null;

    return this.amplifyService.auth()
      .confirmSignIn(user, user.verificationCode, mfaType)
      .then((result) => {
        result.source = 'signIn';
        //result.groups = this.getCurrentUserSessionGroup(result.signInUserSession);
        this.changeAuthState('signedIn', result);
      })
      .catch((err) => {

        if (err.code == 'NotAuthorizedException' && err.message == 'Refresh Token has expired') {
          this.router.navigate(['openModal', 'AuthenticatorComponent']);
          this.cognitoCatch(err);
        } else {
          this.cognitoCatch(err);
        }
        throw (err);
      })
      .finally(() => this.als.setLoaded(true, 'auth'));
  }


  /********* SIGNOUT **********/
  async signOut(): Promise<any> {
    await this.als.setLoaded(false, 'auth');
    return this.amplifyService.auth()
      .signOut()
      .catch((err) => this.cognitoCatch(err))
      .finally(() => {
        this.als.setLoaded(true, 'auth');
        this.router.navigate(['/']);
      });
  }

  /********* NEWPASSORD REQUIRED **********/
  async completeNewPassword(user: User): Promise<any> {
    await this.als.setLoaded(false, 'auth');
    let userAttributes = user.challengeName === 'NEW_PASSWORD_REQUIRED' ? user.challengeParam.userAttributes : null;
    delete userAttributes.phone_number_verified;

    return this.amplifyService.auth()
      .completeNewPassword(user, user.password, { name: user.username })
      .then(() => {
        this.changeAuthState('signIn', user);
        this.setMessage({ code: 'passwordFormat', notReset: true, msg: 'Senha atualizada, faça o login novamente.' });
      })
      .catch((err) => this.cognitoCatch(err))
      .finally(() => this.als.setLoaded(true, 'auth'));
  }
  /********* FORGOT PASSWORD *********/
  async forgotPassword(user: User): Promise<any> {
    await this.als.setLoaded(false, 'auth');
    return this.amplifyService.auth()
      .forgotPassword(user.username)
      .catch((err) => this.cognitoCatch(err))
      .finally(() => this.als.setLoaded(true, 'auth'));
  }

  async forgotPasswordSubmit(user: User): Promise<any> {
    await this.als.setLoaded(false, 'auth');
    return this.amplifyService.auth()
      .forgotPasswordSubmit(user.username, user.verificationCode, user.password)
      .then(() => {
        this.setMessage({ code: 'passwordFormat', notReset: true, msg: 'Senha atualizada, faça o login novamente.' });
        this.changeAuthState('signIn', user);
        throw (user);
      })
      .catch((err) => this.cognitoCatch(err))
      .finally(() => this.als.setLoaded(true, 'auth'));
  }




  cognitoCatch(err) {
    console.log('COGNITO CATCH', err, this);
    if (typeof err == 'string')
      err = { code: err };

    switch (err.code) {
      case 'NetworkError':
        this.setMessage({ code: err.code, msg: 'Falha de conexão, tente novamente mais tarde.' });
        break;
      case 'TooManyFailedAttemptsException':
        this.setMessage({ code: err.code, msg: 'Tentativas de senha excedida, tente novamente mais tarde.' });
        break;
      case 'UserNotFoundException':
        this.setMessage({ code: err.code, msg: 'Usuário e/ou senha incorreto' });
        break;
      case "NotAuthorizedException":
        if (err.message == 'User is disabled.')
          this.setMessage({ code: err.code, msg: 'Acesso não autorizado, usuário bloqueado.' });
        else if (err.message == 'Password attempts exceeded')
          this.setMessage({ code: err.code, msg: 'Acesso não autorizado, muitas tentativas de acesso' });
        else if (err.message == 'Refresh Token has expired')
          this.setMessage({ code: err.code, msg: 'Acesso expirado, faça o login novamente' });
        else
          this.setMessage({ code: err.code, msg: 'Usuário e/ou senha incorreto' });
        break;
      case "UserNotConfirmedException":
        this.setMessage({ code: err.code, msg: 'Valide seu cadastro, enviamos um código para seu e-mail.' });
        break;
      case "TooManyRequestsException":
        this.setMessage({ code: err.code, msg: 'Muitas tentativas de acesso' });
        break;
      case "CodeMismatchException":
        this.setMessage({ code: err.code, msg: 'Código incorreto, tente novamente' });
        break;
      case "InvalidPasswordException":
        this.setMessage({ code: 'passwordFormat', msg: 'Digite uma senha com 8 caracteres, letras e números.' });
        break;
      case 'PasswordResetRequiredException':
        this.setMessage({ code: err.code, msg: 'Troca de senha necessária' });
        break;
      case "LimitExceededException":
        this.setMessage({ code: err.code, msg: 'Limite de tentativa excedido. Tente novamente mais tarde' });
        break;
      case "UserNotFoundException":
        this.setMessage({ code: err.code, msg: 'Preencha o e-mail corretamente' });
        break;
      case "not authenticated":

        break;
      default:
        console.log('ERRO NÃO TRATADO', err);
        break;
    }
    throw (err);
  }


  getInitialAuthState(): AuthState {
    const as = new AuthState();
    as.state = AuthState.INITIAL;
    as.user = null;
    return as;
  }

  getDefaultAction(): any[] {
    if (this.actualAuthState.state == 'signedIn')
      return [this.signOutAction];

    return [this.signInAction];
  }

  getSignInAction(): AuthAction {
    return this.signInAction;
  }

  getSignOutAction(): any {
    return this.signOutAction;
  }
}

abstract class AuthAction extends ModalAction {
  constructor(
    group: string,
    label: string,
    goTo: any,
    params: any = [],
    protected authService: AuthService
  ) {
    super(group, label, goTo, params);
  }
}

class SignInAction extends AuthAction {

  constructor(authService: AuthService) {
    super('auth', 'Login', 'AuthenticatorComponent', { class: 'signin' }, authService);
  }

  execute() {
    this.authService.changeAuthState(AuthState.SIGN_IN, null);
  }
}

class SignOutAction extends Action {

  constructor(protected authService: AuthService) {
    super('auth', 'Sair', ['signOut'], { class: 'signout' });
  }

  execute() {
    this.authService.signOut();
  }
}